import { useState } from 'react';
import { OverlayScrollArea, useDebounce } from '@odo/shared-ui';
import { CheckIcon } from '@heroicons/react/24/outline';
import { useCharges } from '../../../../hooks/useBilling';
import { formatMoneyDollars } from '../../../../utils/money';
import type { ChargeListItem } from '../../../../services/billing';
import { CREATE_PO_FIELD_LABEL_CLASS } from './createPoFieldStyles';

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_LIMIT = 30;

interface SelectChargeStepProps {
    practiceId: string;
    chargeId: string | null;
    onChange: (charge: ChargeListItem) => void;
}

function ChargeRow({
    charge,
    selected,
    onSelect
}: {
    charge: ChargeListItem;
    selected: boolean;
    onSelect: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onSelect}
            className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors ${
                selected ? 'bg-gray-50' : ''
            }`}
        >
            <span className="min-w-0">
                <span className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                    {charge.patientName ?? <span className="text-gray-400">No patient</span>}
                    <span className="text-xs font-normal text-gray-400">
                        {charge.lineItemCount} line{charge.lineItemCount === 1 ? '' : 's'}
                    </span>
                </span>
                <span className="block truncate text-xs text-gray-500 mt-0.5">
                    {charge.description ?? charge.productSummary ?? 'No description'}
                </span>
            </span>
            <span className="flex items-center gap-3 shrink-0">
                <span className="text-sm font-semibold text-gray-900 tabular-nums">
                    {formatMoneyDollars(charge.totalAmount)}
                </span>
                {selected && <CheckIcon className="h-4 w-4 text-gray-700" />}
            </span>
        </button>
    );
}

export function SelectChargeStep({ practiceId, chargeId, onChange }: SelectChargeStepProps) {
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);

    const { charges, isLoading } = useCharges({
        practiceId,
        page: 1,
        limit: PAGE_LIMIT,
        q: debouncedSearch || undefined,
        sort: 'date',
        order: 'desc'
    });

    return (
        <div>
            <label className={CREATE_PO_FIELD_LABEL_CLASS}>Charge *</label>
            <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search patient or description…"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm mb-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
            <div className="border border-gray-200 rounded-lg overflow-hidden">
                <OverlayScrollArea contentClassName="max-h-72" trackInsetPx={4}>
                    {isLoading ? (
                        <div className="px-4 py-6 text-center text-sm text-gray-400">Loading…</div>
                    ) : charges.length === 0 ? (
                        <div className="px-4 py-6 text-center text-sm text-gray-400">
                            No charges found for this practice.
                        </div>
                    ) : (
                        charges.map((charge) => (
                            <ChargeRow
                                key={charge.id}
                                charge={charge}
                                selected={charge.id === chargeId}
                                onSelect={() => onChange(charge)}
                            />
                        ))
                    )}
                </OverlayScrollArea>
            </div>
        </div>
    );
}
